<script setup lang="ts">
/**
 * Модальное окно администрирования видов нежелательных событий (НС).
 *
 * Функционал:
 * - Отображение интерактивного дерева видов НС с возможностью зума и панорамирования;
 * - Создание новых и редактирование существующих видов НС через боковую панель (сайдбар);
 * - Назначение основного координатора и дополнительных ответственных с привязкой отделений;
 * - Drag-and-Drop перемещение категорий по иерархии дерева с сохранением через adverseSettingsStore;
 * - Фильтрация дерева по подстроке поиска и переключение режимов отображения (все / свои / глобальные);
 * - Управление компактным видом и скрытием иерархии с сохранением настроек в localStorage;
 * - Предупреждение о несохраненных изменениях при закрытии (ConfirmDialog).
 */

// Импорты Vue и утилит
import { isAxiosError } from 'axios'
import { computed, nextTick, provide, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'

// Импорты UI-компонентов
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import EmployeeSelectionModal from '@/components/createdUi/EmployeeSelectionModal.vue'
import NewTreeMultiSelectComponent from '@/components/createdUi/NewTreeMultiSelectComponent.vue'
import { omitTreeIds, searchAdverseEventTypes, searchDepartments } from '@/components/createdUi/treeSearch'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import { useDialogDismissGuard } from '@/composables/useDialogDismissGuard'

// Импорты хранилищ
import { useAdverseSettingsStore } from '@/modules/adverseEvents/stores/adverseSettingsStore'
import { questionnairesApi } from '@/modules/adverseEvents/questionnaires'
import type { ITreeNode } from '@/modules/apiStore/types/ITreeNode'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'

// Импорты типов и форматтеров
import type {
  IAdverseEventResponsibleEmployee,
  IAdverseEventTypeSettingsOption,
  IAdverseEventTypeTreeNode,
} from '@/modules/adverseEvents/types/IAdverseSettings'
import { parseFullName } from '@/utils/formatters'
import { getRandomAvatar } from '@/utils/getRandomAvatar'
import AdverseEventTypeTreeNode from './AdverseEventTypeTreeNode.vue'
import { ADVERSE_ANCHORS } from '@/modules/onboarding/scenarios/adverseEvents/anchors'

/**
 * Пропсы модального окна администрирования видов нежелательных событий
 */
const props = defineProps<{
  /** Управление видимостью диалогового окна */
  modelValue: boolean
}>()

/** Доступные опции переключателя режимов отображения видов НС */
const settingsOptions: IAdverseEventTypeSettingsOption[] = [
  { value: 'all', label: 'Все' },
  { value: 'custom', label: 'Свои' },
  { value: 'expert', label: 'Глобальные' },
]

/**
 * События компонента
 */
const emit = defineEmits<{
  /** Обновление состояния видимости модалки (v-model) */
  'update:modelValue': [value: boolean]
}>()

/** Утилиты адаптивности экрана Vuetify */
const { smAndDown } = useDisplay()

/** Хранилища состояния */
const apiStore = useApiStore()
const adverseSettingsStore = useAdverseSettingsStore()
const feedbackStore = useFeedbackStore()

// ==========================================
// Состояние данных дерева и формы
// ==========================================

/** Данные дерева категорий из стора */
const treeData = computed(() => adverseSettingsStore.treeData)

/** Глобальная системная настройка видов при создании НС ('all' | 'custom' | 'expert') */
const availableEventTypesSetting = ref<string>('all')

/** Локальный фильтр отображения дерева видов ('all' | 'custom' | 'expert') */
const treeFilterMode = ref<string>('all')

/** Поисковый запрос */
const searchQuery = ref('')

/** Текущий выбранный для редактирования узел */
const selectedNode = ref<IAdverseEventTypeTreeNode | null>(null)

/** ID родительской категории в форме редактирования ('root-virtual' для корня) */
const localParentId = ref<string | null>('root-virtual')

/** Название вида НС в форме */
const localName = ref('')

/** Описание вида НС в форме */
const localDescription = ref('')
const questionnaireOptions = ref<{ id: number; name: string }[]>([])
const localQuestionnaireIds = ref<number[]>([])

/** Порядок сортировки */
const localOrder = ref<number>(0)

/** Флаг активности вида */
const localIsActive = ref(true)

/** Режим создания новой категории (true) или редактирования (false) */
const isCreateMode = ref(false)

/** Вид из ДаоМед — защищены название, описание и иерархия; разрешены активность, порядок, последствия и координаторы */
const isDaomedMain = computed(() => !isCreateMode.value && !!selectedNode.value?.is_daomed_main)

/** Флаг открытия сайдбара редактирования/создания */
const isSidebarOpen = ref(false)

// ==========================================
// Настройки отображения (localStorage)
// ==========================================

/** Состояние компактного вида (синхронизация с localStorage) */
const isCompactModeActive = ref(localStorage.getItem('adverse_event_types_tree_compact_mode') === 'true')

watch(isCompactModeActive, newVal => {
  localStorage.setItem('adverse_event_types_tree_compact_mode', String(newVal))
})

/** Состояние скрытия иерархии (синхронизация с localStorage) */
const isHideHierarchyActive = ref(localStorage.getItem('adverse_event_types_tree_hide_hierarchy') === 'true')

watch(isHideHierarchyActive, newVal => {
  localStorage.setItem('adverse_event_types_tree_hide_hierarchy', String(newVal))
  if (newVal) {
    collapsedNodes.value = getAllNodeIds(treeData.value)
  } else {
    collapsedNodes.value = []
  }
})

/** Состояние drag & drop перетаскивания (синхронизация с localStorage) */
const isDragDropEnabled = ref(localStorage.getItem('adverse_event_types_tree_drag_drop') === 'true')

/** Фильтр «Глобальные» — DnD бессмысленен (все узлы is_daomed_main) */
const isGlobalTreeFilter = computed(() => treeFilterMode.value === 'expert')

watch(isDragDropEnabled, newVal => {
  localStorage.setItem('adverse_event_types_tree_drag_drop', String(newVal))
})

watch(isGlobalTreeFilter, isGlobal => {
  if (isGlobal) isDragDropEnabled.value = false
})

provide('isCompactModeActive', isCompactModeActive)
provide('isDragDropEnabled', isDragDropEnabled)
provide('searchQuery', searchQuery)
provide('onToggleNodeActive', async (node: IAdverseEventTypeTreeNode, isActive: boolean) => {
  await adverseSettingsStore.toggleAdverseEventTypeActive(node.id, isActive)
  if (selectedNode.value && String(selectedNode.value.id) === String(node.id))
    localIsActive.value = isActive
})

// ==========================================
// Форма ответственных и валидация
// ==========================================

/** Локальная копия ответственных координаторов */
const localResponsibles = ref<IAdverseEventResponsibleEmployee[]>([])

/** Локальное значение оценки последствий */
const localConsequence = ref<number | null>(null)

/** Видимость выпадающих списков выбора отделений */
const visibleDropdowns = ref<Record<number, boolean>>({})

/** Индикатор процесса сохранения */
const savingChanges = ref(false)

/** Флаг отображения ошибок валидации */
const showValidationErrors = ref(false)

/** Ссылка на компонент модального окна выбора сотрудников */
const employeeSelectionModalRef = ref<any>(null)

/** Флаг: выбирается ли сейчас основной координатор (true) или дополнительный (false) */
const isSelectingPrimary = ref(false)

/**
 * Открыть модалку выбора дополнительного координатора
 */
function openEmployeeSelectionModal() {
  isSelectingPrimary.value = false
  employeeSelectionModalRef.value?.open()
}

/**
 * Открыть модалку выбора основного координатора
 */
function openPrimaryEmployeeSelection() {
  isSelectingPrimary.value = true
  employeeSelectionModalRef.value?.open()
}

/**
 * Добавить выбранного сотрудника в список координаторов вида НС
 *
 * @param employee Выбранный объект сотрудника
 */
function handleAddResponsible(employee: any) {
  const { first_name, last_name, middle_name } = parseFullName(employee.full_name)

  if (isSelectingPrimary.value) {
    // Удаляем предыдущего основного ответственного, если он был
    localResponsibles.value = localResponsibles.value.filter(r => !r.is_default)

    const newPrimary: IAdverseEventResponsibleEmployee = {
      id: -Date.now(),
      is_default: true,
      departments: [],
      employee: {
        id: employee.id,
        first_name,
        last_name,
        middle_name,
        avatar: employee.avatar || '',
        position: employee.position || { name: '' },
      },
    }
    localResponsibles.value.push(newPrimary)
  } else {
    const alreadyExists = localResponsibles.value.some(r => r.employee.id === employee.id && !r.is_default)
    if (alreadyExists) return

    const newResponsible: IAdverseEventResponsibleEmployee = {
      id: -Date.now(),
      is_default: false,
      departments: [],
      employee: {
        id: employee.id,
        first_name,
        last_name,
        middle_name,
        avatar: employee.avatar || '',
        position: employee.position || { name: '' },
      },
    }
    localResponsibles.value.push(newResponsible)
  }
}

// ==========================================
// Диалог подтверждения и удаление ответственных
// ==========================================

/** Флаг видимости подтверждения закрытия диалога с несохраненными данными */
const isConfirmDialogVisible = ref(false)

/** Тип действия, ожидающего подтверждения пользователя */
const pendingAction = ref<'close_modal' | 'close_sidebar' | null>(null)

/** Флаг видимости подтверждения удаления координатора */
const isRemoveConfirmVisible = ref(false)

/** Координатор, намеченный к удалению */
const responsibleToRemove = ref<IAdverseEventResponsibleEmployee | null>(null)

/**
 * Запрос на удаление координатора из списка формы
 *
 * @param resp Объект ответственного координатора
 */
function requestRemoveResponsible(resp: IAdverseEventResponsibleEmployee) {
  responsibleToRemove.value = resp
  isRemoveConfirmVisible.value = true
}

/**
 * Обработка ответа пользователя в диалоге подтверждения удаления координатора
 *
 * @param confirmed Результат подтверждения (true/false)
 */
function onConfirmRemove(confirmed: boolean) {
  isRemoveConfirmVisible.value = false
  if (confirmed && responsibleToRemove.value) {
    localResponsibles.value = localResponsibles.value.filter(r => r.id !== responsibleToRemove.value!.id)
  }
  responsibleToRemove.value = null
}

/**
 * Сформировать полное ФИО сотрудника
 *
 * @param emp Объект сотрудника
 * @returns Строка с полным именем
 */
function getEmployeeFullName(emp: any): string {
  if (!emp) return ''
  return [emp.last_name, emp.first_name, emp.middle_name].filter(Boolean).join(' ')
}

/**
 * Централизованная обработка ошибок API с выводом тоста
 *
 * @param error Объект ошибки
 * @param defaultMessage Сообщение по умолчанию
 */
function handleError(error: any, defaultMessage: string) {
  if (isAxiosError(error)) {
    const apiError = error.response?.data?.errors?.[0]?.detail
                     || error.response?.data?.message
                     || error.message
                     || defaultMessage
    feedbackStore.showToast({
      type: 'error',
      message: apiError,
      time: 7000,
    })
  } else {
    feedbackStore.showToast({
      type: 'error',
      message: defaultMessage,
      time: 7000,
    })
  }
}

// ==========================================
// Зум и отображение
// ==========================================

/** Массив идентификаторов свернутых узлов дерева */
const collapsedNodes = ref<Array<string>>([])

/** Контейнер полотна дерева для скролла и зума */
const treeContainerOuter = ref<HTMLElement | null>(null)

/** Текущий масштаб отображения дерева в процентах (10..500) */
const zoom = ref(smAndDown.value ? 50 : 100)

/** Строковое представление масштаба для поля ввода */
const zoomInput = ref(String(zoom.value))

/**
 * Увеличить масштаб дерева на 10%
 */
function zoomIn() {
  if (zoom.value < 500) {
    zoom.value = Math.min(500, zoom.value + 10)
  }
}

/**
 * Уменьшить масштаб дерева на 10%
 */
function zoomOut() {
  if (zoom.value > 10) {
    zoom.value = Math.max(10, zoom.value - 10)
  }
}

watch(zoom, async newZoom => {
  zoomInput.value = String(newZoom)

  const container = treeContainerOuter.value
  if (!container) return

  const oldScrollWidth = container.scrollWidth
  const oldScrollHeight = container.scrollHeight

  const scrollCenterX = container.scrollLeft + container.clientWidth / 2
  const scrollCenterY = container.scrollTop + container.clientHeight / 2

  const ratioX = oldScrollWidth > 0 ? scrollCenterX / oldScrollWidth : 0.5
  const ratioY = oldScrollHeight > 0 ? scrollCenterY / oldScrollHeight : 0.5

  await nextTick()

  const newScrollWidth = container.scrollWidth
  const newScrollHeight = container.scrollHeight

  container.scrollLeft = ratioX * newScrollWidth - container.clientWidth / 2
  container.scrollTop = ratioY * newScrollHeight - container.clientHeight / 2
})

/**
 * Обработка ввода масштаба вручную с валидацией границ
 */
function onZoomInputBlur() {
  let val = Number.parseInt(zoomInput.value, 10)
  if (isNaN(val)) {
    val = zoom.value
  } else {
    val = Math.max(10, Math.min(500, val))
  }
  zoom.value = val
  zoomInput.value = String(val)
}

// ==========================================
// Перетаскивание полотна дерева (drag-to-scroll)
// ==========================================

/** Флаг активного перетаскивания холста */
const isDragging = ref(false)
let startX = 0
let startY = 0
let scrollLeftStart = 0
let scrollTopStart = 0
let hasMoved = false

/**
 * Старт перетаскивания холста мышью
 *
 * @param e MouseEvent
 */
function startDrag(e: MouseEvent) {
  if (e.button !== 0) return

  const target = e.target as HTMLElement
  if (target.closest('.dept-node-card')) return

  const container = treeContainerOuter.value
  if (!container) return

  isDragging.value = true
  hasMoved = false

  startX = e.pageX - container.offsetLeft
  startY = e.pageY - container.offsetTop
  scrollLeftStart = container.scrollLeft
  scrollTopStart = container.scrollTop
}

/**
 * Перемещение холста при зажатой левой кнопке мыши
 *
 * @param e MouseEvent
 */
function onDrag(e: MouseEvent) {
  if (!isDragging.value) return

  const container = treeContainerOuter.value
  if (!container) return

  e.preventDefault()

  const x = e.pageX - container.offsetLeft
  const y = e.pageY - container.offsetTop

  const walkX = x - startX
  const walkY = y - startY

  if (Math.abs(walkX) > 3 || Math.abs(walkY) > 3) {
    hasMoved = true
  }

  container.scrollLeft = scrollLeftStart - walkX
  container.scrollTop = scrollTopStart - walkY
}

/**
 * Завершение перетаскивания холста
 */
function stopDrag() {
  isDragging.value = false
}

/**
 * Предотвращение ложного клика после перемещения холста
 *
 * @param e MouseEvent
 */
function onContainerClick(e: MouseEvent) {
  if (hasMoved) {
    e.stopPropagation()
    e.preventDefault()
    hasMoved = false
  }
}

// ==========================================
// Drag & Drop логика для узлов дерева
// ==========================================

/** ID перетаскиваемого узла */
const draggedNodeId = ref<string | null>(null)

/** ID узла-цели */
const dragOverNodeId = ref<string | null>(null)

/** Объект перетаскиваемого узла */
const draggedNode = ref<IAdverseEventTypeTreeNode | null>(null)

/** Запрещенные для drop идентификаторы узлов (потомки и сам узел) */
const disallowedTargetIds = ref<Set<string>>(new Set())

/**
 * Рекурсивный сбор ID всех потомков узла для кэширования запрещенных целей
 *
 * @param node Корневой узел поддерева
 * @returns Множество ID потомков
 */
function getDescendantIds(node: IAdverseEventTypeTreeNode): Set<string> {
  const ids = new Set<string>()
  const collect = (n: IAdverseEventTypeTreeNode) => {
    if (n.children) {
      for (const child of n.children) {
        ids.add(child.id)
        collect(child)
      }
    }
  }
  collect(node)
  return ids
}

/**
 * Проверка: разрешен ли сброс (drop) в данный целевой узел
 *
 * @param targetId ID целевого узла
 * @returns true, если сброс разрешен
 */
function isAllowedDrop(targetId: string): boolean {
  if (!draggedNodeId.value) return false
  if (draggedNode.value?.is_daomed_main) return false
  if (targetId !== 'root-virtual') {
    const target = findNodeInTreeById(treeData.value, targetId)
    if (target?.is_daomed_main) return false
  }
  return !disallowedTargetIds.value.has(targetId)
}

/**
 * Переместить узел дерева в новую родительскую категорию и сохранить на сервере
 *
 * @param draggedId ID перетаскиваемого узла
 * @param targetId ID целевого родительского узла (или 'root-virtual' для переноса в корень)
 */
async function moveNode(draggedId: string, targetId: string) {
  try {
    await adverseSettingsStore.moveAdverseEventType(draggedId, targetId)
    await adverseSettingsStore.fetchAdverseEventTypeTree(getTreeFilterParams())
    feedbackStore.showToast({
      type: 'success',
      message: 'Категория успешно перемещена',
      time: 4000,
    })
  } catch (error) {
    console.error('❌ Ошибка при перемещении категории дерева:', error)
    handleError(error, 'Не удалось переместить категорию')
  }
}

// ==========================================
// Предоставление зависимостей (Provide)
// ==========================================

provide('draggedNodeId', draggedNodeId)
provide('dragOverNodeId', dragOverNodeId)
provide('draggedNode', draggedNode)

/** Обработчик старта перетаскивания узла дерева */
provide('onNodeDragStart', (node: IAdverseEventTypeTreeNode) => {
  if (node.is_daomed_main) return
  draggedNodeId.value = node.id
  draggedNode.value = node

  // Кэшируем список запрещенных ID для O(1) проверок при dragover
  const disallowed = getDescendantIds(node)
  disallowed.add(node.id)
  disallowed.add('root-virtual')
  disallowedTargetIds.value = disallowed
})

/** Обработчик наведения на узел при перетаскивании */
provide('onNodeDragOver', (node: IAdverseEventTypeTreeNode) => {
  if (isAllowedDrop(node.id)) {
    dragOverNodeId.value = node.id
  } else {
    dragOverNodeId.value = null
  }
})

/** Обработчик ухода курсора с узла при перетаскивании */
provide('onNodeDragLeave', () => {
  dragOverNodeId.value = null
})

/** Обработчик сброса узла на другой узел дерева */
provide('onNodeDrop', (targetNode: IAdverseEventTypeTreeNode) => {
  if (draggedNodeId.value && isAllowedDrop(targetNode.id)) {
    moveNode(draggedNodeId.value, targetNode.id)
  }
  draggedNodeId.value = null
  draggedNode.value = null
  dragOverNodeId.value = null
  disallowedTargetIds.value.clear()
})

/** Обработчик завершения перетаскивания узла */
provide('onNodeDragEnd', () => {
  draggedNodeId.value = null
  draggedNode.value = null
  dragOverNodeId.value = null
  disallowedTargetIds.value.clear()
})

/**
 * Обработчик сброса узла на свободное пространство контейнера (перенос в корень)
 *
 * @param e Событие DragEvent
 */
function onContainerDrop(e: DragEvent) {
  const target = e.target as HTMLElement
  if (target.closest('.dept-node-card')) {
    return
  }
  if (draggedNodeId.value) {
    moveNode(draggedNodeId.value, 'root-virtual')
  }
  draggedNodeId.value = null
  draggedNode.value = null
  dragOverNodeId.value = null
  disallowedTargetIds.value.clear()
}

// ==========================================
// Параметры фильтрации и настройки
// ==========================================

/**
 * Получение GET-параметров фильтрации дерева видов НС
 */
function getTreeFilterParams(): Record<string, any> {
  const params: Record<string, any> = {}
  if (treeFilterMode.value === 'custom') {
    params.is_daomed_main = false
  } else if (treeFilterMode.value === 'expert') {
    params.is_daomed_main = true
  }
  return params
}

/**
 * Обработчик изменения глобальной настройки видов при создании НС
 *
 * @param newValue Новое значение настройки ('all' | 'custom' | 'expert')
 */
async function onAvailableEventTypesSettingChanged(newValue: any) {
  if (!newValue) return

  try {
    availableEventTypesSetting.value = newValue
    await adverseSettingsStore.updateAdverseSettings({ event_types: newValue })
  } catch (error) {
    console.error('❌ Ошибка при изменении настройки видов НС:', error)
    handleError(error, 'Не удалось сохранить настройку')
  }
}

/**
 * Обработчик изменения локального фильтра дерева видов НС
 *
 * @param newValue Новое значение фильтра ('all' | 'custom' | 'expert')
 */
async function onTreeFilterChanged(newValue: any) {
  if (!newValue) return

  try {
    treeFilterMode.value = newValue
    await adverseSettingsStore.fetchAdverseEventTypeTree(getTreeFilterParams())
    if (isHideHierarchyActive.value) {
      collapsedNodes.value = getAllNodeIds(treeData.value)
    }
  } catch (error) {
    console.error('❌ Ошибка при фильтрации дерева видов НС:', error)
    handleError(error, 'Не удалось применить фильтр')
  }
}

// ==========================================
// Управление закрытием и диалогами подтверждения
// ==========================================

/**
 * Безусловное закрытие модального окна
 */
function closeModal() {
  isSidebarOpen.value = false
  selectedNode.value = null
  emit('update:modelValue', false)
}

/**
 * Проверка несохраненных изменений перед закрытием модалки
 */
function handleCloseModal() {
  if (isSidebarOpen.value && isChanged.value) {
    pendingAction.value = 'close_modal'
    isConfirmDialogVisible.value = true
  } else {
    closeModal()
  }
}

/**
 * Проверка несохраненных изменений перед закрытием боковой панели
 */
function handleCloseSidebar() {
  if (isChanged.value) {
    pendingAction.value = 'close_sidebar'
    isConfirmDialogVisible.value = true
  } else {
    isSidebarOpen.value = false
    selectedNode.value = null
  }
}

/** Защитный хук от случайного клика вне сайдбара */
const onSidebarDismissAttempt = useDialogDismissGuard(handleCloseSidebar, 'AdverseEventTypesModalSidebar')

/**
 * Обработка ответа пользователя в диалоге подтверждения отмены изменений
 *
 * @param confirmed true, если пользователь подтвердил закрытие
 */
function onConfirmClose(confirmed: boolean) {
  isConfirmDialogVisible.value = false
  if (confirmed) {
    if (pendingAction.value === 'close_sidebar') {
      isSidebarOpen.value = false
      selectedNode.value = null
    } else if (pendingAction.value === 'close_modal') {
      closeModal()
    }
  }
  pendingAction.value = null
}

// ==========================================
// Вспомогательные методы работы с деревом
// ==========================================

/**
 * Поиск идентификатора родительского узла по ID дочернего элемента
 *
 * @param nodes Массив узлов дерева
 * @param childId Идентификатор дочернего узла
 * @returns ID родительского узла или null, если узел в корне
 */
function findParentNodeId(nodes: IAdverseEventTypeTreeNode[], childId: string): string | null {
  for (const node of nodes) {
    if (node.children && node.children.length > 0) {
      for (const child of node.children) {
        if (String(child.id) === String(childId)) {
          return node.id
        }
      }
      const found = findParentNodeId(node.children, childId)
      if (found) return found
    }
  }
  return null
}

/**
 * Иерархическое дерево доступных родителей для выбора в форме (с исключением себя и потомков)
 */
const availableParentTree = computed(() => {
  if (!selectedNode.value) return []

  const disallowed = getDescendantIds(selectedNode.value)
  disallowed.add(selectedNode.value.id)

  function filterNodes(nodes: IAdverseEventTypeTreeNode[]): any[] {
    const result: any[] = []
    for (const node of nodes) {
      if (!disallowed.has(node.id)) {
        const filteredNode: any = {
          key: String(node.id),
          label: node.name,
          data: {
            name: node.name,
            selfId: String(node.id),
          },
          children: [],
        }
        if (node.children && node.children.length > 0) {
          filteredNode.children = filterNodes(node.children)
        }
        result.push(filteredNode)
      }
    }
    return result
  }

  return [
    {
      key: 'root-virtual',
      label: 'Без родителя (корень)',
      data: {
        name: 'Без родителя (корень)',
        selfId: 'root-virtual',
      },
      children: [],
    },
    ...filterNodes(treeData.value),
  ]
})

async function searchParentTypes(query: string): Promise<ITreeNode[]> {
  if (!selectedNode.value)
    return []

  const disallowed = getDescendantIds(selectedNode.value)
  disallowed.add(String(selectedNode.value.id))
  const tree = await searchAdverseEventTypes(query)

  return [
    {
      key: 'root-virtual',
      label: 'Без родителя (корень)',
      data: {
        name: 'Без родителя (корень)',
        selfId: 'root-virtual',
      },
      children: [],
    },
    ...omitTreeIds(tree, disallowed),
  ]
}

/**
 * Выбор узла для просмотра и редактирования в боковой панели
 *
 * @param node Объект выбранного узла дерева
 */
function selectNode(node: IAdverseEventTypeTreeNode) {
  isCreateMode.value = false
  showValidationErrors.value = false
  selectedNode.value = node
  localName.value = node.name || ''
  localDescription.value = node.description || ''
  localQuestionnaireIds.value = [...(node.questionnaire_ids || [])]
  localOrder.value = node.order !== undefined && node.order !== null ? node.order : 0
  localIsActive.value = node.is_active !== false
  localConsequence.value = node.consequence !== undefined && node.consequence !== null ? node.consequence : null

  const pId = findParentNodeId(treeData.value, node.id)
  localParentId.value = pId === null ? 'root-virtual' : pId

  localResponsibles.value = (node.responsible_employees || []).map(r => ({
    id: r.id,
    is_default: r.is_default,
    departments: r.departments ? r.departments.map(d => ({ ...d })) : [],
    employee: { ...r.employee },
  }))
  visibleDropdowns.value = {}
  isSidebarOpen.value = true
}

/**
 * Инициализация формы создания нового вида НС в боковой панели
 */
function openCreateNode() {
  isCreateMode.value = true
  showValidationErrors.value = false
  selectedNode.value = {
    id: 'new-node',
    name: '',
    description: '',
    children: [],
    responsible_employees: [],
  }
  localName.value = ''
  localDescription.value = ''
  localQuestionnaireIds.value = []
  localOrder.value = 0
  localIsActive.value = true
  localConsequence.value = null
  localParentId.value = 'root-virtual'
  localResponsibles.value = []
  visibleDropdowns.value = {}
  isSidebarOpen.value = true
}

/**
 * Рекурсивный поиск узла в дереве по его ID
 *
 * @param nodes Массив узлов для поиска
 * @param id Идентификатор искомого узла
 * @returns Найденный узел или null
 */
function findNodeInTreeById(nodes: IAdverseEventTypeTreeNode[], id: string): IAdverseEventTypeTreeNode | null {
  for (const node of nodes) {
    if (String(node.id) === String(id)) {
      return node
    }
    if (node.children && node.children.length > 0) {
      const found = findNodeInTreeById(node.children, id)
      if (found) return found
    }
  }
  return null
}

/**
 * Получение плоского списка всех идентификаторов узлов дерева (для полного сворачивания)
 *
 * @param nodes Массив узлов дерева
 * @returns Массив всех ID узлов
 */
function getAllNodeIds(nodes: IAdverseEventTypeTreeNode[]): Array<string> {
  const ids: Array<string> = []
  function traverse(items: IAdverseEventTypeTreeNode[]) {
    for (const item of items) {
      if (item && item.id) {
        ids.push(item.id)
      }
      if (item && item.children && item.children.length) {
        traverse(item.children)
      }
    }
  }
  traverse(nodes)
  return ids
}

/**
 * Поиск узла дерева отделений по его ключу
 *
 * @param nodes Иерархический массив отделений
 * @param id Идентификатор отделения
 * @returns Объект узла отделения или null
 */
function findDeptNodeById(nodes: any[], id: string): any {
  for (const node of nodes) {
    if (String(node.key) === String(id)) {
      return node
    }
    if (node.children && node.children.length > 0) {
      const found = findDeptNodeById(node.children, id)
      if (found) return found
    }
  }
  return null
}

/**
 * Синхронизация выбранных отделений для дополнительного координатора
 *
 * @param resp Объект ответственного сотрудника
 * @param deptIds Массив выбранных ID отделений
 */
function onDeptsSelected(resp: IAdverseEventResponsibleEmployee, deptIds: string[]) {
  if (!deptIds || !deptIds.length) {
    resp.departments = []
    return
  }
  const selectedDepts: { id: string; name: string }[] = []
  for (const id of deptIds) {
    const foundNode = findDeptNodeById(apiStore.departmentsTree, id)
    if (foundNode) {
      selectedDepts.push({
        id: String(id),
        name: foundNode.label,
      })
    }
  }
  resp.departments = selectedDepts
}

/**
 * Признак наличия несохраненных изменений в форме боковой панели
 */
const isChanged = computed(() => {
  if (!selectedNode.value) return false
  if (JSON.stringify([...localQuestionnaireIds.value].sort()) !== JSON.stringify([...(selectedNode.value.questionnaire_ids || [])].sort())) return true

  if (localName.value !== (selectedNode.value.name || '')) return true
  if (localDescription.value !== (selectedNode.value.description || '')) return true

  const originalOrder = selectedNode.value.order !== undefined && selectedNode.value.order !== null ? selectedNode.value.order : 0
  if (originalOrder !== localOrder.value) return true

  const originalIsActive = selectedNode.value.is_active !== false
  if (originalIsActive !== localIsActive.value) return true

  const originalParentId = findParentNodeId(treeData.value, selectedNode.value.id)
  const originalParentNormalized = originalParentId === null ? 'root-virtual' : originalParentId
  if (originalParentNormalized !== localParentId.value) return true

  const originalConsequence = selectedNode.value.consequence !== undefined && selectedNode.value.consequence !== null ? selectedNode.value.consequence : null
  const currentConsequence = localConsequence.value !== undefined && localConsequence.value !== null ? localConsequence.value : null
  if (originalConsequence !== currentConsequence) return true

  const original = selectedNode.value.responsible_employees || []
  if (original.length !== localResponsibles.value.length) return true

  for (const localResp of localResponsibles.value) {
    const origResp = original.find(o => o.id === localResp.id)
    if (!origResp) return true

    const localDepts = localResp.departments || []
    const origDepts = origResp.departments || []
    if (localDepts.length !== origDepts.length) return true

    const localDeptIds = localDepts.map(d => String(d.id)).sort()
    const origDeptIds = origDepts.map(d => String(d.id)).sort()

    for (let i = 0; i < localDeptIds.length; i++) {
      if (localDeptIds[i] !== origDeptIds[i]) return true
    }
  }
  return false
})

/**
 * Сохранение изменений вида НС (создание или обновление) с валидацией
 */
async function saveChanges() {
  if (!selectedNode.value || !isChanged.value) return

  if (!isDaomedMain.value && !localName.value.trim()) {
    feedbackStore.showToast({
      type: 'error',
      message: 'Заполните название',
      time: 4000,
    })
    return
  }

  const hasEmptyDepartments = localOtherResponsibles.value.some(r => !r.departments || r.departments.length === 0)
  if (hasEmptyDepartments) {
    showValidationErrors.value = true
    feedbackStore.showToast({
      type: 'error',
      message: 'Заполните отделения для всех координаторов',
      time: 5000,
    })
    return
  }

  savingChanges.value = true
  try {
    const currentParentId = localParentId.value === 'root-virtual' ? null : localParentId.value

    // Формируем payload ответственных
    const responsibleEmployees = localResponsibles.value.map(r => {
      const item: any = {
        is_default: r.is_default || false,
        employee: { id: r.employee.id },
        departments: (r.departments || []).map(d => ({ id: d.id })),
      }
      if (r.id > 0) {
        item.id = r.id
      }
      return item
    })

    const payload: any = isDaomedMain.value
      ? {
          is_active: localIsActive.value,
          consequence: localConsequence.value,
          order: Number(localOrder.value) || 0,
          responsible_employees: responsibleEmployees,
        }
      : {
          parent_id: currentParentId,
          name: localName.value,
          description: localDescription.value,
          is_active: localIsActive.value,
          consequence: localConsequence.value,
          order: Number(localOrder.value) || 0,
          responsible_employees: responsibleEmployees,
          responsible_positions: [],
        }

    payload.questionnaire_ids = localQuestionnaireIds.value

    if (isCreateMode.value) {
      await adverseSettingsStore.createAdverseEventType(payload)
      feedbackStore.showToast({
        type: 'success',
        message: 'Вид НС успешно создан!',
        time: 5000,
      })
    } else {
      await adverseSettingsStore.updateAdverseEventType(selectedNode.value.id, payload)
      feedbackStore.showToast({
        type: 'success',
        message: 'Изменения успешно сохранены!',
        time: 5000,
      })
    }

    isSidebarOpen.value = false
    selectedNode.value = null
    savingChanges.value = false

    await adverseSettingsStore.fetchAdverseEventTypeTree(getTreeFilterParams())
  } catch (error) {
    console.error('❌ Ошибка при сохранении изменений:', error)
    handleError(error, 'Не удалось сохранить изменения')
  } finally {
    savingChanges.value = false
  }
}

/**
 * Переключение сворачивания / разворачивания дочернего поддерева узла
 *
 * @param nodeId Идентификатор узла
 */
function toggleNodeCollapse(nodeId: string) {
  const container = treeContainerOuter.value
  const card = document.getElementById(`dept-card-${nodeId}`)

  let initialOffset = 0
  let containerLeft = 0
  let hasLayoutInfo = false

  if (container && card) {
    initialOffset = card.getBoundingClientRect().left
    containerLeft = container.getBoundingClientRect().left
    hasLayoutInfo = true
  }

  const index = collapsedNodes.value.indexOf(nodeId)
  if (index === -1) {
    collapsedNodes.value.push(nodeId)
  } else {
    collapsedNodes.value.splice(index, 1)
  }

  if (hasLayoutInfo) {
    nextTick(() => {
      const newCard = document.getElementById(`dept-card-${nodeId}`)
      const newContainer = treeContainerOuter.value
      if (newContainer && newCard) {
        const newAbsoluteLeft = newCard.getBoundingClientRect().left - newContainer.getBoundingClientRect().left + newContainer.scrollLeft
        newContainer.scrollLeft = newAbsoluteLeft - (initialOffset - containerLeft)
      }
    })
  }
}

// ==========================================
// Поиск и фильтрация
// ==========================================

/**
 * Проверка узла на соответствие строке поискового запроса по всем полям
 *
 * @param node Узел дерева
 * @param queryStr Строка поиска в нижнем регистре
 * @returns true, если узел или его атрибуты соответствуют поиску
 */
function isNodeMatchingQuery(node: IAdverseEventTypeTreeNode, queryStr: string): boolean {
  if (!queryStr) return true

  // 1. Имя категории
  if ((node.name || '').toLowerCase().includes(queryStr)) return true

  // 2. Описание категории
  if ((node.description || '').toLowerCase().includes(queryStr)) return true

  // 3. Последствия по умолчанию
  if (node.consequence !== undefined && node.consequence !== null) {
    const consequenceObj = apiStore.adverseEventConsequences.find(c => c.value === node.consequence)
    if (consequenceObj && (consequenceObj.label || '').toLowerCase().includes(queryStr)) {
      return true
    }
  }

  // 4. Координаторы (ответственные) и их отделения
  if (node.responsible_employees && node.responsible_employees.length > 0) {
    for (const r of node.responsible_employees) {
      if (!r.employee) continue

      // ФИО сотрудника
      const fullName = [r.employee.last_name, r.employee.first_name, r.employee.middle_name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      if (fullName.includes(queryStr)) return true

      // Должность
      if (r.employee.position?.name && r.employee.position.name.toLowerCase().includes(queryStr)) {
        return true
      }

      // Отделения
      if (r.departments && r.departments.length > 0) {
        for (const d of r.departments) {
          if ((d.name || '').toLowerCase().includes(queryStr)) return true
          if ((d.short_name || '').toLowerCase().includes(queryStr)) return true
        }
      }
    }
  }

  return false
}

/**
 * Рекурсивная фильтрация иерархии дерева категорий по подстроке поиска
 *
 * @param nodes Массив узлов дерева
 * @param queryStr Поисковая подстрока
 * @returns Отфильтрованный массив узлов
 */
function filterTreeLocal(nodes: IAdverseEventTypeTreeNode[], queryStr: string): IAdverseEventTypeTreeNode[] {
  return (nodes || [])
    .map(node => {
      if (!node) return null
      const clonedNode = { ...node }
      if (clonedNode.children && clonedNode.children.length > 0) {
        clonedNode.children = filterTreeLocal(clonedNode.children, queryStr)
      } else {
        clonedNode.children = []
      }

      const isSelfMatch = isNodeMatchingQuery(clonedNode, queryStr)
      const hasMatchingChildren = clonedNode.children.length > 0

      if (isSelfMatch || hasMatchingChildren) {
        return clonedNode
      }
      return null
    })
    .filter(Boolean) as IAdverseEventTypeTreeNode[]
}

/**
 * Вычисляемое отфильтрованное дерево для рендера
 */
const filteredTreeData = computed<IAdverseEventTypeTreeNode[]>(() => {
  const query = searchQuery.value?.trim().toLowerCase()
  if (!query) {
    return treeData.value
  }
  return filterTreeLocal(treeData.value, query)
})

/** Основной координатор вида НС в форме редактирования */
const localPrimaryResponsible = computed(() => {
  return localResponsibles.value.find(r => r.is_default === true) || null
})

/** Дополнительные координаторы с привязкой отделений в форме */
const localOtherResponsibles = computed(() => {
  return localResponsibles.value.filter(r => r.is_default !== true)
})

/**
 * Центрирование полотна дерева по горизонтали
 */
const centerTree = () => {
  let attempts = 0
  const interval = setInterval(() => {
    const el = treeContainerOuter.value
    if (el && el.scrollWidth > 0 && el.clientWidth > 0) {
      el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2
      el.scrollTop = 0
      attempts++
      if (attempts >= 15) {
        clearInterval(interval)
      }
    }
  }, 40)

  setTimeout(() => clearInterval(interval), 1000)
}

// ==========================================
// Наблюдатели (Watchers)
// ==========================================

/**
 * Отслеживание открытия модального окна для сброса и загрузки данных
 */
watch(() => props.modelValue, async isOpen => {
  if (isOpen) {
    showValidationErrors.value = false
    searchQuery.value = ''
    zoom.value = smAndDown.value ? 50 : 100
    isSidebarOpen.value = false
    selectedNode.value = null
    collapsedNodes.value = []

    // Загружаем сохраненный компактный режим и режим drag & drop из localStorage
    isCompactModeActive.value = localStorage.getItem('adverse_event_types_tree_compact_mode') === 'true'
    isDragDropEnabled.value = localStorage.getItem('adverse_event_types_tree_drag_drop') === 'true'

    const settingsPromises: Promise<any>[] = [
      adverseSettingsStore.fetchAdverseSettings(),
      questionnairesApi.options().then(options => { questionnaireOptions.value = options }),
    ]

    if (!apiStore.departmentsTree || !apiStore.departmentsTree.length) {
      settingsPromises.push(apiStore.fetchAllDepartments())
    }

    if (!apiStore.adverseEventConsequences || !apiStore.adverseEventConsequences.length) {
      settingsPromises.push(apiStore.fetchProbabilityAndConsequence())
    }

    try {
      const [settingsData] = await Promise.all(settingsPromises)
      availableEventTypesSetting.value = settingsData?.event_types ?? 'all'
      treeFilterMode.value = 'all'

      await adverseSettingsStore.fetchAdverseEventTypeTree(getTreeFilterParams())

      if (isHideHierarchyActive.value) {
        collapsedNodes.value = getAllNodeIds(treeData.value)
      }
      nextTick(() => {
        centerTree()
      })
    } catch (error) {
      console.error('❌ Ошибка при загрузке данных модального окна:', error)
    }
  }
})

/**
 * Отслеживание поискового запроса для автоматического раскрытия узлов дерева
 */
watch(searchQuery, newQuery => {
  const query = newQuery?.trim().toLowerCase()
  if (query) {
    collapsedNodes.value = []
  } else {
    collapsedNodes.value = []
    centerTree()
  }
})

// Маппинг тяжести последствий
function getConsequenceLabel(consequence: number): string {
  const consequenceLabels: Record<number, string> = {
    0: 'Несущественные',
    1: 'Легкие',
    2: 'Средние',
    3: 'Значительные',
    4: 'Катастрофические',
  }
  return consequenceLabels[consequence] || 'Неизвестно'
}

function getConsequenceColor(consequence: number): string {
  const consequenceColors: Record<number, string> = {
    0: '#8A8D93',
    1: '#56CA00',
    2: '#FFB400',
    3: '#FF7C02',
    4: '#FF4C51',
  }
  return consequenceColors[consequence] || '#8A8D93'
}

function getInitials(emp: any): string {
  const first = emp.first_name ? emp.first_name.charAt(0) : ''
  const last = emp.last_name ? emp.last_name.charAt(0) : ''
  return `${last}${first}`.toUpperCase()
}

function getAvatarUrl(emp: any): string {
  return getRandomAvatar()
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    scrollable
    content-class="structure-modal-dialog"
    :scrim="smAndDown"
    persistent
    fullscreen
    :z-index="2400"
    @update:model-value="handleCloseModal"
  >
    <VCard class="structure-modal-card rounded-lg pa-0 d-flex flex-column position-relative">
      <!-- Шапка: Название и Закрытие (всегда вверху) -->
      <div class="d-flex align-center justify-space-between px-6 py-4 border-b">
        <span class="text-h4 font-weight-bold">Настройки видов НС</span>
        <VBtn
          icon="ri-close-line"
          variant="text"
          density="comfortable"
          @click="handleCloseModal"
        />
      </div>

      <!-- Панель фильтров и действий -->
      <div
        class="d-flex border-b gap-4 transition-all"
        :class="smAndDown ? 'flex-column align-stretch px-4 py-3' : 'align-center px-6 py-3 flex-wrap'"
      >
        <!-- Дропдаун 1: Глобальная настройка опций при создании НС (выделен цветом) -->
        <div
          class="global-setting-wrapper d-flex align-center gap-2"
          :data-onboarding="ADVERSE_ANCHORS.settings.typesShowInForm"
          :style="smAndDown ? 'width: 100%;' : 'max-width: 320px; min-width: 270px; width: 100%;'"
        >
          <VSelect
            v-model="availableEventTypesSetting"
            :items="settingsOptions"
            item-title="label"
            item-value="value"
            label="Отображать в форме создания НС"
            density="compact"
            variant="outlined"
            color="info"
            class="global-setting-select"
            hide-details
            :menu-props="{ contentClass: 'entity-form-menu' }"
            @update:model-value="onAvailableEventTypesSettingChanged"
          />
        </div>

        <!-- Дропдаун 2: Локальный фильтр отображения дерева -->
        <div
          class="tree-filter-wrapper d-flex align-center gap-2"
          :data-onboarding="ADVERSE_ANCHORS.settings.typesView"
          :style="smAndDown ? 'width: 100%;' : 'max-width: 180px; min-width: 150px; width: 100%;'"
        >
          <VSelect
            v-model="treeFilterMode"
            :items="settingsOptions"
            item-title="label"
            item-value="value"
            label="Фильтр дерева"
            density="compact"
            variant="outlined"
            hide-details
            class="tree-filter-select"
            :menu-props="{ contentClass: 'entity-form-menu' }"
            @update:model-value="onTreeFilterChanged"
          />
        </div>

        <!-- Чекбокс Отображать компактно -->
        <VCheckbox
          v-model="isCompactModeActive"
          label="Отображать компактно"
          hide-details
          density="compact"
          class="compact-checkbox"
          :class="smAndDown ? 'w-100' : ''"
        />

        <!-- Чекбокс Скрывать иерархию -->
        <VCheckbox
          v-model="isHideHierarchyActive"
          label="Скрывать иерархию"
          hide-details
          density="compact"
          class="compact-checkbox"
          :class="smAndDown ? 'w-100' : ''"
        />

        <!-- Чекбокс Включить перетаскивание -->
        <div
          class="d-flex align-center ga-1 flex-shrink-0"
          :class="smAndDown ? 'w-100' : ''"
          :data-onboarding="ADVERSE_ANCHORS.settings.typesViewLast"
        >
          <VCheckbox
            v-model="isDragDropEnabled"
            label="Включить перетаскивание"
            hide-details
            density="compact"
            class="compact-checkbox"
            :disabled="isGlobalTreeFilter"
          />
          <VTooltip
            v-if="isGlobalTreeFilter"
            location="top"
          >
            <template #activator="{ props: tipProps }">
              <span
                v-bind="tipProps"
                class="d-inline-flex"
              >
                <VIcon
                  icon="ri-information-line"
                  size="16"
                  color="secondary"
                />
              </span>
            </template>
            Перемещение глобальных видов запрещено. Можно только менять их активность
          </VTooltip>
        </div>

        <VTextField
          v-model="searchQuery"
          placeholder="Поиск"
          prepend-inner-icon="ri-search-line"
          variant="underlined"
          density="compact"
          class="search-underlined"
          :class="smAndDown ? 'w-100' : 'ms-auto'"
          :style="smAndDown ? 'max-width: 100%;' : 'max-width: 320px; width: 100%;'"
          clearable
          hide-details
        />

        <VBtn
          color="primary"
          prepend-icon="ri-add-line"
          height="36"
          class="text-none px-4"
          :class="smAndDown ? 'w-100 mt-2' : ''"
          :data-onboarding="ADVERSE_ANCHORS.settings.typesCreate"
          @click="openCreateNode"
        >
          Создать
        </VBtn>
      </div>

      <!-- Основная область с деревом -->
      <VCardText class="pa-0 flex-grow-1 overflow-hidden" :data-onboarding="ADVERSE_ANCHORS.settings.types">
        <div class="d-flex h-100 align-stretch position-relative">
          <div class="pa-6 d-flex flex-column h-100 overflow-hidden flex-grow-1 position-relative">
            <!-- Дерево с прокруткой и перетаскиванием -->
            <div
              ref="treeContainerOuter"
              class="tree-container-outer w-100 h-100 flex-grow-1 overflow-auto"
              :class="{
                'is-dragging-process': draggedNodeId !== null,
              }"
              @mousedown="startDrag"
              @mousemove="onDrag"
              @mouseup="stopDrag"
              @mouseleave="stopDrag"
              @click.capture="onContainerClick"
              @dragover.prevent
              @drop="onContainerDrop"
            >
              <div class="tree-container">
                <div class="tree d-inline-flex flex-column align-center mx-auto" :style="{ zoom: zoom / 100 }">
                  <ul v-if="filteredTreeData && filteredTreeData.length">
                    <AdverseEventTypeTreeNode
                      v-for="rootNode in filteredTreeData"
                      :key="rootNode.id"
                      :node="rootNode"
                      :selected-node-id="isSidebarOpen && selectedNode ? String(selectedNode.id) : null"
                      :collapsed-nodes="collapsedNodes"
                      @select="selectNode"
                      @toggle-collapse="toggleNodeCollapse"
                    />
                  </ul>
                </div>
              </div>
            </div>

            <!-- Панель масштабирования (слева снизу, видима всегда) -->
            <div class="zoom-control-panel d-flex align-center bg-surface border rounded px-1 py-0.5 shadow-sm">
              <VBtn
                icon="ri-add-line"
                variant="text"
                density="comfortable"
                :disabled="zoom >= 500"
                class="zoom-btn"
                @click="zoomIn"
              />
              <div class="d-flex align-center justify-center mx-1" style="min-width: 50px;">
                <input
                  v-model="zoomInput"
                  type="text"
                  class="zoom-input text-body-2 font-weight-bold text-center"
                  style="width: 32px; outline: none; border: none; background: transparent; text-align: center; color: inherit;"
                  @blur="onZoomInputBlur"
                  @keydown.enter="onZoomInputBlur"
                >
                <span class="text-body-2 font-weight-bold select-none" style="margin-left: -2px;">%</span>
              </div>
              <VBtn
                icon="ri-subtract-line"
                variant="text"
                density="comfortable"
                :disabled="zoom <= 10"
                class="zoom-btn"
                @click="zoomOut"
              />
            </div>
          </div>
        </div>
      </VCardText>

      <!-- Модальное окно детальной информации -->
      <VDialog
        :model-value="isSidebarOpen && selectedNode !== null"
        persistent
        width="1200"
        max-width="90vw"
        @click:outside="handleCloseSidebar"
        @keydown.esc.stop="handleCloseSidebar"
      >
        <VCard v-if="selectedNode" class="pa-0 rounded-lg d-flex flex-column" style="max-height: 90vh; overflow: hidden;">
          <!-- Заголовок модального окна -->
          <VCardTitle class="d-flex pa-6">
            <div class="form_card d-flex justify-space-between align-center w-100">
              <div class="form_card__title">
                <span class="text-h4">
                  {{ isCreateMode ? 'Создание вида НС' : 'Редактирование вида НС' }}
                </span>
              </div>
              <CustomCloseButton :close-action="handleCloseSidebar" />
            </div>
          </VCardTitle>

          <!-- Контент модального окна -->
          <VCardText class="overflow-y-auto flex-grow-1">
            <VCol class="d-flex flex-column gap-6 px-0">
              <!-- Название -->
              <VTextarea
                v-model="localName"
                placeholder="Введите название"
                variant="outlined"
                density="comfortable"
                persistent-placeholder
                rows="1"
                auto-grow
                no-resize
                hide-details
                :disabled="isDaomedMain"
              >
                <template #label>
                  <span>Название<sup class="required-label">*</sup></span>
                </template>
              </VTextarea>

              <!-- Описание -->
              <VTextarea
                v-model="localDescription"
                placeholder="Введите описание"
                variant="outlined"
                density="comfortable"
                persistent-placeholder
                rows="3"
                hide-details
                :disabled="isDaomedMain"
              >
                <template #label>
                  <span>Описание</span>
                </template>
              </VTextarea>

              <div class="d-flex align-start gap-2">
                <VSelect
                  v-model="localQuestionnaireIds"
                  :items="questionnaireOptions"
                  item-title="name"
                  item-value="id"
                  label="Анкеты"
                  multiple
                  chips
                  closable-chips
                  clearable
                  class="flex-grow-1"
                  :menu-props="{ contentClass: 'entity-form-menu' }"
                />
                <VTooltip max-width="350" open-on-click>
                  <template #activator="{ props: helpProps }">
                    <VBtn v-bind="helpProps" variant="text" color="secondary" icon="ri-information-line" size="24" aria-label="Подсказка: анкеты" />
                  </template>
                  Выбранные анкеты доступны для этого вида и его подвидов. Общие вопросы добавляются автоматически.
                </VTooltip>
              </div>

              <!-- Иерархия, Порядок и Активность -->
              <div class="d-flex align-center gap-4" :class="{ 'flex-column align-stretch': smAndDown }">
                <div class="flex-grow-1">
                  <NewTreeComponent
                    placeholder="Без родителя (корень)"
                    label="Иерархия"
                    search-placeholder="Поиск по категориям..."
                    :selected="{ value: localParentId && localParentId !== 'root-virtual' ? { id: String(localParentId) } : null }"
                    :tree-data="availableParentTree"
                    :search-tree="searchParentTypes"
                    :disabled="isDaomedMain"
                    @update:selected="(val) => localParentId = val ? val.id : 'root-virtual'"
                  />
                </div>
                <div :style="smAndDown ? 'width: 100%;' : 'max-width: 150px; width: 100%;'">
                  <VTextField
                    v-model.number="localOrder"
                    type="number"
                    variant="outlined"
                    density="comfortable"
                    persistent-placeholder
                    hide-details
                  >
                    <template #label>
                      <span>Порядок</span>
                    </template>
                  </VTextField>
                </div>
                <div class="d-flex align-center">
                  <VCheckbox
                    v-model="localIsActive"
                    label="Активен"
                    hide-details
                    density="comfortable"
                  />
                </div>
              </div>

              <!-- Всего координаторов -->
              <div class="d-flex align-center gap-2 font-weight-medium" style="font-size: 15px; color: rgb(var(--v-theme-primary)) !important;">
                <VIcon icon="ri-team-line" size="20" style="color: rgb(var(--v-theme-primary)) !important;" />
                <span>Всего координаторов: {{ localResponsibles.length }}</span>
              </div>

              <!-- Секция: Координатор по умолчанию -->
              <div class="d-flex flex-column gap-3">
                <span class="text-uppercase text-caption text-medium-emphasis" style="letter-spacing: 0.8px; opacity: 0.8;">
                  Координатор по умолчанию
                </span>
                <div
                  v-if="localPrimaryResponsible"
                  class="d-flex align-center justify-space-between w-100 ga-2"
                >
                  <div class="d-flex align-center gap-3 employee-item min-w-0">
                    <VAvatar size="40" variant="tonal">
                      <VImg :src="getAvatarUrl(localPrimaryResponsible.employee)" />
                    </VAvatar>
                    <div class="text-truncate">
                      <div class="text-body-1 text-truncate text-high-emphasis" style="font-size: 15px;">
                        {{ localPrimaryResponsible.employee.last_name }} {{ localPrimaryResponsible.employee.first_name }} {{ localPrimaryResponsible.employee.middle_name || '' }}
                      </div>
                    </div>
                  </div>
                  <div class="d-flex align-center ga-2 flex-shrink-0">
                    <VBtn
                      size="small"
                      variant="outlined"
                      prepend-icon="ri-pencil-line"
                      @click="openPrimaryEmployeeSelection"
                    >
                      Изменить
                    </VBtn>
                    <VBtn
                      size="small"
                      variant="outlined"
                      color="error"
                      prepend-icon="ri-delete-bin-line"
                      @click="requestRemoveResponsible(localPrimaryResponsible)"
                    >
                      Удалить
                    </VBtn>
                  </div>
                </div>
                <div
                  v-else
                  class="d-flex align-center justify-space-between w-100 ga-2"
                >
                  <VChip
                    color="error"
                    size="small"
                    variant="tonal"
                    class="rounded"
                  >
                    Не назначен
                  </VChip>
                  <VBtn
                    size="small"
                    variant="outlined"
                    prepend-icon="ri-add-line"
                    @click="openPrimaryEmployeeSelection"
                  >
                    Назначить
                  </VBtn>
                </div>
              </div>

              <!-- Секция: Последствия по умолчанию -->
              <VSelect
                v-model="localConsequence"
                :items="apiStore.adverseEventConsequences"
                clearable
                item-title="label"
                item-value="value"
                density="comfortable"
                variant="outlined"
                placeholder="Не указаны"
                persistent-placeholder
                hide-details
                :menu-props="{ contentClass: 'entity-form-menu' }"
              >
                <template #label>
                  <span>Последствия по умолчанию</span>
                </template>
              </VSelect>

              <!-- Секция: Ответственные -->
              <div v-if="localOtherResponsibles.length > 0" class="d-flex flex-column gap-3">
                <VTable class="bg-transparent w-100 custom-activity-table">
                  <thead>
                    <tr>
                      <th class="text-caption font-weight-medium text-high-emphasis text-left px-3 text-no-wrap" style="width: 220px;">
                        КООРДИНАТОР ПО ОТДЕЛЕНИЯМ
                      </th>
                      <th class="text-caption font-weight-medium text-high-emphasis text-left px-3">
                        ОТДЕЛЕНИЯ
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="resp in localOtherResponsibles" :key="resp.id">
                      <td class="py-2 px-3 text-body-2 text-high-emphasis text-no-wrap">
                        <div class="d-flex align-center justify-space-between w-100">
                          <div class="d-flex align-center gap-2">
                            <VAvatar size="32" variant="tonal">
                              <VImg :src="getAvatarUrl(resp.employee)" />
                            </VAvatar>
                            <div class="text-truncate" style="max-width: 130px;">
                              <div class="text-body-2 text-truncate text-high-emphasis font-weight-medium">
                                {{ resp.employee.last_name }} {{ resp.employee.first_name.charAt(0) }}.{{ resp.employee.middle_name ? `${resp.employee.middle_name.charAt(0)}.` : '' }}
                              </div>
                            </div>
                          </div>
                          <VIcon
                            icon="ri-delete-bin-line"
                            size="18"
                            class="cursor-pointer text-error ms-2"
                            @click="requestRemoveResponsible(resp)"
                          />
                        </div>
                      </td>
                      <td class="py-2 px-3">
                        <NewTreeMultiSelectComponent
                          placeholder="Выбрать"
                          :selected="resp.departments ? resp.departments.map(d => d.id) : []"
                          :tree-data="apiStore.departmentsTree"
                          :search-tree="searchDepartments"
                          search-placeholder="Поиск по отделениям"
                          :error="showValidationErrors && (!resp.departments || resp.departments.length === 0)"
                          class="dept-tree-select"
                          @update:selected="(deptIds) => onDeptsSelected(resp, deptIds)"
                        />
                      </td>
                    </tr>
                  </tbody>
                </VTable>
              </div>
            </VCol>
          </VCardText>

          <!-- Кнопки действий внизу сайдбара -->
          <div
            class="border-t px-6 py-4 bg-surface mt-auto d-flex"
            :class="smAndDown ? 'flex-column gap-6' : 'flex-row align-center gap-4'"
          >
            <VBtn
              variant="outlined"
              color="primary"
              prepend-icon="ri-user-add-line"
              :block="smAndDown"
              :style="smAndDown ? undefined : { flex: '1 1 0', minWidth: 0 }"
              @click="openEmployeeSelectionModal"
            >
              Добавить координатора
            </VBtn>
            <VBtn
              color="primary"
              :block="smAndDown"
              :style="smAndDown ? undefined : { flex: '1 1 0', minWidth: 0 }"
              :disabled="!isChanged || savingChanges"
              :loading="savingChanges"
              @click="saveChanges"
            >
              Сохранить
            </VBtn>
          </div>
        </VCard>
      </VDialog>
    </VCard>
  </VDialog>

  <ConfirmDialog
    v-model:is-dialog-visible="isConfirmDialogVisible"
    confirmation-question="Закрыть без сохранения?"
    confirmation-text="У вас есть несохраненные изменения. Вы действительно хотите закрыть окно без сохранения?"
    confirm-btn="Закрыть"
    reject-btn="Отмена"
    @confirm="onConfirmClose"
  />

  <ConfirmDialog
    v-model:is-dialog-visible="isRemoveConfirmVisible"
    confirmation-question="Удалить координатора?"
    :confirmation-text="`Вы действительно хотите удалить координатора ${responsibleToRemove ? getEmployeeFullName(responsibleToRemove.employee) : ''}?`"
    confirm-btn="Удалить"
    reject-btn="Отмена"
    @confirm="onConfirmRemove"
  />

  <EmployeeSelectionModal
    ref="employeeSelectionModalRef"
    :employees="apiStore.employees"
    title="Выбор координатора"
    placeholder="Поиск по ФИО..."
    @select="handleAddResponsible"
  />
</template>

<style lang="scss" scoped>
.structure-modal-card {
  height: 100% !important;
  max-height: 100% !important;
}

.transition-all {
  transition: all 0.25s ease-in-out;
}

.bg-light {
  background-color: rgba(var(--v-theme-on-surface), 0.02) !important;
}

:deep(.search-underlined.v-text-field) {
  .v-field__control {
    height: 36px !important;
  }
  .v-field__prepend-inner {
    align-self: center !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
  .v-field__input {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    min-height: 36px !important;
    align-self: center !important;
    display: flex !important;
    align-items: center !important;
  }
}

.tree-container-outer {
  &.cursor-grab {
    cursor: grab;
  }

  &.cursor-grabbing {
    cursor: grabbing;
    user-select: none;
  }

  &.is-dragging-process {
    :deep(.dept-node-card) * {
      pointer-events: none !important;
    }
  }
}

.tree-container {
  min-width: 100%;
  width: max-content;
  display: flex;
  justify-content: safe center;
  padding: 40px 80px;
}

.tree :deep(ul) {
  padding-top: 32px;
  position: relative;
  transition: all 0.5s;
  display: flex;
  justify-content: center;
  gap: 60px;
  padding-left: 0;
  margin: 0;
  width: max-content;
  min-width: 100%;
}

.tree :deep(li) {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding-top: 32px;
  list-style-type: none;
  flex-shrink: 0;
  width: min-content;
}

.tree :deep(ul:has(> .has-subdepartments ~ .has-subdepartments)) {
  & > li {
    width: auto;
  }
  & > li > ul {
    width: auto;
    min-width: 0;
  }
}

/* Линии */
.tree :deep(li::before), .tree :deep(li::after) {
  content: '';
  position: absolute;
  top: 0;
  right: 50%;
  border-top: 2px solid rgb(var(--v-theme-primary));
  width: calc(50% + 30px);
  height: 32px;
  z-index: 1;
}

.tree :deep(li::after) {
  right: auto;
  left: 50%;
  border-left: 2px solid rgb(var(--v-theme-primary));
}

.tree :deep(li:first-child::after) {
  border-radius: 16px 0 0 0;
}

.tree :deep(li:last-child::before) {
  border-right: 2px solid rgb(var(--v-theme-primary));
  border-radius: 0 16px 0 0;
  border-left: none;
}

.tree :deep(li:only-child::after), .tree :deep(li:only-child::before) {
  display: none;
}

.tree :deep(li:only-child) {
  padding-top: 0;
}

.tree :deep(li:first-child::before), .tree :deep(li:last-child::after) {
  border: 0 none;
}

.tree :deep(ul::before) {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  border-left: 2px solid rgb(var(--v-theme-primary));
  width: 0;
  height: 32px;
  z-index: 1;
}

.tree > ul::before {
  display: none !important;
}

.tree > ul > li::before,
.tree > ul > li::after {
  display: none !important;
}

.tree > ul > li {
  padding-top: 0 !important;
}

.tree :deep(.tree-node-wrapper) {
  position: relative;
  z-index: 2;
}

.tree :deep(.tree-node-wrapper::after) {
  content: '';
  position: absolute;
  bottom: -32px;
  left: 50%;
  border-left: 2px solid rgb(var(--v-theme-primary));
  width: 0;
  height: 32px;
  z-index: 1;
  display: none;
}

.tree :deep(.tree-node-wrapper.has-children::after) {
  display: block;
}

/* Карточки */
.tree :deep(.dept-node-card) {
  width: 380px;
  height: 312px;
  background: rgb(var(--v-theme-surface));
  border-radius: 6px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.25s ease-in-out;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.tree :deep(.dept-node-card.compact) {
  height: auto !important;
}

.tree :deep(.dept-node-card.compact .bottom-action-btn) {
  margin-top: 16px !important;
}

.tree :deep(.dept-node-card:hover) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(var(--v-theme-primary), 0.15);
  border-color: rgb(var(--v-theme-primary)) !important;
}

.tree :deep(.dept-node-card.selected) {
  border-color: rgb(var(--v-theme-primary-300)) !important;
  box-shadow: 0 0 0 8px rgb(var(--v-theme-primary-300)), 0 4px 12px rgba(46, 38, 61, 0.2) !important;
}

.tree :deep(.dept-node-card.is-dragging-origin) {
  opacity: 0.25 !important;
  border: 1px dashed rgba(var(--v-theme-on-surface), 0.3) !important;
  background: rgba(var(--v-theme-surface), 0.4) !important;
}

.tree :deep(.dept-node-card.is-dragging-over) {
  transform: scale(0.9) !important;
  opacity: 0.5 !important;
  border-style: dashed !important;
  border-color: rgba(var(--v-theme-primary), 0.5) !important;
}

.tree :deep(.dept-node-card.drag-over) {
  border: 2px dashed rgb(var(--v-theme-primary)) !important;
  background-color: rgba(var(--v-theme-primary), 0.05) !important;
  box-shadow: 0 0 12px rgba(var(--v-theme-primary), 0.2) !important;
  transform: translateY(-4px) scale(1.02) !important;
}

@keyframes bounceDown {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(3px); }
}

@keyframes bounceUp {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}

.tree :deep(.animate-bounce-down) {
  animation: bounceDown 0.8s infinite ease-in-out;
}

.tree :deep(.animate-bounce-up) {
  animation: bounceUp 0.8s infinite ease-in-out;
}

.tree :deep(li .dept-node-card::before) {
  content: '';
  position: absolute;
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  border-width: 6px 6px 0 6px;
  border-style: solid;
  border-color: rgb(var(--v-theme-primary)) transparent transparent transparent;
  width: 0;
  height: 0;
  z-index: 3;
}

.tree :deep(.bottom-action-btn) {
  margin: 0 16px 16px 16px;
  border-radius: 6px;
  background-color: rgba(var(--v-theme-primary), 0.08) !important;
  color: rgb(var(--v-theme-primary)) !important;
  transition: all 0.2s ease;
  cursor: pointer;
  margin-top: auto;
  flex-shrink: 0;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tree :deep(.bottom-action-btn:hover:not(.disabled)) {
  background-color: rgba(var(--v-theme-primary), 0.12) !important;
}

.tree :deep(.bottom-action-btn.disabled) {
  margin: 0 16px 16px 16px;
  margin-top: auto;
  flex-shrink: 0;
  border-radius: 6px;
  background-color: rgba(var(--v-theme-on-surface), 0.03);
  color: rgba(var(--v-theme-on-surface), 0.4);
  cursor: default;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tree :deep(.accent-color) {
  color: rgb(var(--v-theme-primary)) !important;
}

.zoom-control-panel {
  position: absolute;
  left: 24px;
  bottom: 48px;
  z-index: 10;
  width: fit-content;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  background-color: rgb(var(--v-theme-surface)) !important;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;

  .zoom-btn {
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
    padding: 0 !important;
    color: rgba(var(--v-theme-on-surface), 0.6) !important;

    &:hover {
      color: rgb(var(--v-theme-primary)) !important;
    }
  }
}

.risk-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.employee-item {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.custom-activity-table {
  border-collapse: collapse;
  table-layout: fixed !important;
  width: 100% !important;

  :deep(.v-table__wrapper table thead tr th) {
    --v-overlay-scrim-opacity: 1 !important;
    background: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
    height: 46px !important;
    border-bottom: none !important;
    position: relative;
  }

  :deep(.v-table__wrapper table thead tr th:not(:last-child))::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    width: 2px;
    height: 14px;
    background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
  }

  :deep(thead tr) {
    height: 46px !important;
    &:hover {
      background: transparent !important;
    }
  }

  :deep(tbody) {
    tr {
      height: 46px !important;
      &:hover {
        background: transparent !important;
      }
    }

    td {
      height: auto !important;
      border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;
    }
  }

  @media (max-width: 959px) {
    display: block !important;

    :deep(.v-table__wrapper) {
      display: block !important;
      table {
        display: block !important;
      }
    }

    :deep(thead) {
      display: none !important;
    }

    :deep(tbody) {
      display: block !important;

      tr {
        display: flex !important;
        flex-direction: column !important;
        height: auto !important;
        padding: 12px 0 !important;
        border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;
        gap: 8px !important;

        td {
          display: block !important;
          width: 100% !important;
          padding: 0 !important;
          border-bottom: none !important;

          .dept-tree-select {
            width: 100% !important;
          }
        }
      }
    }
  }
}

.dept-tree-select {
  width: 100%;
  max-width: 400px;
  min-width: 0;

  :deep(.v-field) {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    min-height: 32px !important;
    height: auto !important;
    --v-field-padding-bottom: 0px !important;
    --v-input-padding-top: 0px !important;
    border: none !important;
  }
  :deep(.v-field__input) {
    min-height: 32px !important;
    height: auto !important;
    padding-top: 6px !important;
    padding-bottom: 6px !important;
    font-size: 13px !important;
    line-height: 1.4 !important;
  }
  :deep(.v-field__append-inner) {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    align-self: center !important;
  }
}

.tree :deep(.tree-node-wrapper::after),
.tree :deep(.tree-node-wrapper.has-children::after) {
  display: none !important;
}

.tree :deep(li.has-subdepartments) {
  align-items: flex-start !important;
}

.tree :deep(ul ul) {
  display: flex !important;
  flex-direction: column !important;
  align-items: flex-start !important;
  padding-left: 50px !important; /* Сдвиг вложенного списка на 50px вправо */
  padding-top: 32px !important;
  gap: 24px !important;
  position: relative !important;
  width: max-content !important; /* Заставляем ширину ul рассчитываться по контенту для автораздвигания соседних веток */
  margin: 0 !important;

  &::before {
    display: none !important;
  }
}

.tree :deep(ul ul::after) {
  display: none !important;
}

.tree :deep(ul ul > li) {
  display: flex !important;
  flex-direction: column !important;
  align-items: flex-start !important;
  padding-top: 0 !important;
  padding-left: 0 !important;
  width: max-content !important;
  position: relative !important;
}

.tree :deep(ul ul .tree-node-wrapper) {
  margin-left: 0 !important; /* Дочерняя карточка прижата к левому краю своего li */
  margin-right: 0 !important;
  position: relative !important;
}

/* Вертикальный ствол родителя на каждом li (сдвинут на 20px вправо от левого края родительской карточки, то есть на -30px от дочернего li) */
/* Вертикальный ствол родителя на каждом li (проходит слева от карточек на расстоянии -30px) */
.tree :deep(ul ul > li::before) {
  content: '' !important;
  position: absolute !important;
  left: -30px !important;
  top: 0 !important;
  bottom: -24px !important; /* Соединяем со следующим ребенком через gap */
  width: 0 !important;
  height: auto !important;
  border-left: 2px solid rgb(var(--v-theme-primary)) !important;
  border-top: none !important;
  border-right: none !important;
  border-bottom: none !important;
  border-radius: 0 !important;
  z-index: 1 !important;
  display: block !important;
}

/* Для первого ребенка линия тянется вверх к родителю */
.tree :deep(ul ul > li:first-child::before) {
  top: -32px !important;
}

/* Отключаем ствол на уровне li для последнего ребенка, так как он рисуется на уровне карточки */
.tree :deep(ul ul > li:last-child::before) {
  display: none !important;
}

/* Горизонтальная отводка от ствола к карточке (выходит из середины карточки влево) */
.tree :deep(ul ul .tree-node-wrapper::after),
.tree :deep(ul ul .tree-node-wrapper.has-children::after) {
  content: '' !important;
  position: absolute !important;
  left: -30px !important; /* От ствола (-30px) к карточке (0) */
  width: 30px !important;
  top: 50% !important;
  height: 0 !important;
  border-top: 2px solid rgb(var(--v-theme-primary)) !important;
  transform: translateY(-50%) !important;
  z-index: 1 !important;
  display: block !important;
}

/* Ствол для последнего ребенка (идет сверху до середины карточки) */
.tree :deep(ul ul > li:last-child .tree-node-wrapper::before) {
  content: '' !important;
  position: absolute !important;
  left: -30px !important;
  top: 0 !important;
  height: 50% !important;
  width: 0 !important;
  border-left: 2px solid rgb(var(--v-theme-primary)) !important;
  z-index: 1 !important;
  display: block !important;
}

/* Ствол для единственного ребенка (идет от родителя (-32px) до середины карточки) */
.tree :deep(ul ul > li:only-child .tree-node-wrapper::before) {
  content: '' !important;
  position: absolute !important;
  left: -30px !important;
  top: -32px !important;
  height: calc(50% + 32px) !important;
  width: 0 !important;
  border-left: 2px solid rgb(var(--v-theme-primary)) !important;
  z-index: 1 !important;
  display: block !important;
}

/* Отключаем старую отводку li::after */
.tree :deep(ul ul > li::after) {
  display: none !important;
}

/* Приглушённые линии и рамка глобальных узлов */
.tree :deep(li.is-daomed-locked-branch::before),
.tree :deep(li.is-daomed-locked-branch::after),
.tree :deep(li.is-daomed-locked-branch > .tree-node-wrapper::before),
.tree :deep(li.is-daomed-locked-branch > .tree-node-wrapper::after),
.tree :deep(li.is-daomed-locked-branch > .tree-node-wrapper.has-children::after),
.tree :deep(li.is-daomed-locked-branch > ul::before),
.tree :deep(ul ul > li.is-daomed-locked-branch::before),
.tree :deep(ul ul > li.is-daomed-locked-branch .tree-node-wrapper::before),
.tree :deep(ul ul > li.is-daomed-locked-branch .tree-node-wrapper::after),
.tree :deep(ul ul > li.is-daomed-locked-branch .tree-node-wrapper.has-children::after),
.tree :deep(ul ul > li.is-daomed-locked-branch:last-child .tree-node-wrapper::before),
.tree :deep(ul ul > li.is-daomed-locked-branch:only-child .tree-node-wrapper::before) {
  border-color: rgba(var(--v-theme-on-surface), 0.2) !important;
  border-left-color: rgba(var(--v-theme-on-surface), 0.2) !important;
  border-top-color: rgba(var(--v-theme-on-surface), 0.2) !important;
  border-right-color: rgba(var(--v-theme-on-surface), 0.2) !important;
}

.tree :deep(li.is-daomed-locked-branch > .dept-node-card::before),
.tree :deep(li.is-daomed-locked-branch .dept-node-card::before) {
  border-color: rgba(var(--v-theme-on-surface), 0.2) transparent transparent transparent !important;
}

.tree :deep(.dept-node-card.is-daomed-locked),
.tree :deep(.dept-node-card.is-daomed-locked:hover),
.tree :deep(.dept-node-card.is-daomed-locked.selected) {
  border-color: rgba(var(--v-theme-on-surface), 0.16) !important;
}

.global-setting-select {
  :deep(.v-field__outline) {
    --v-field-border-opacity: 1 !important;
    color: rgb(var(--v-theme-info)) !important;
  }
  :deep(.v-field-label) {
    color: rgb(var(--v-theme-info)) !important;
    opacity: 1 !important;
    font-size: 13px;
  }
  :deep(.v-select__menu-icon) {
    color: rgb(var(--v-theme-info)) !important;
    opacity: 1 !important;
  }
}

.tree-filter-select {
  :deep(.v-field-label) {
    font-size: 13px;
  }
}

.compact-checkbox {
  flex-shrink: 0;

  :deep(.v-label) {
    white-space: nowrap !important;
  }
}
</style>
